import React from 'react';
import Icon from '../AppIcon';

/**
 * Noticeable realtime toasts for high-priority notifications (e.g. signup leads).
 */
export default function NotificationLiveToasts({ toasts, onOpen, onDismiss }) {
  if (!toasts?.length) return null;

  return (
    <div
      className="pointer-events-none fixed inset-x-3 top-[calc(4.75rem+env(safe-area-inset-top,0px))] z-[12000] flex flex-col items-end gap-2.5 sm:inset-x-auto sm:right-5 sm:top-[calc(5.25rem+env(safe-area-inset-top,0px))]"
      aria-live="polite"
      aria-relevant="additions"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className="pointer-events-auto relative w-full max-w-[22rem] overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_22px_50px_-24px_rgba(11,43,68,0.55)] animate-in fade-in slide-in-from-top-3 duration-300"
        >
          <div
            className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#00adef] via-[#38bdf8] to-[#f4a224]"
            aria-hidden="true"
          />
          <div className="absolute -right-8 -top-10 h-24 w-24 rounded-full bg-[rgba(0,173,239,0.12)] blur-2xl" aria-hidden="true" />
          <div className="absolute -left-10 bottom-0 h-20 w-20 rounded-full bg-[rgba(244,162,36,0.14)] blur-2xl" aria-hidden="true" />

          <button
            type="button"
            onClick={() => onOpen?.(toast)}
            className="relative flex w-full items-start gap-3 px-4 pb-3.5 pt-4 text-left touch-manipulation"
          >
            <span className="relative mt-0.5 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#e8f7fd] to-[#fff4e5] text-[#0284c7] ring-1 ring-[rgba(0,173,239,0.18)]">
              <span className="absolute inset-0 rounded-2xl animate-ping bg-[rgba(0,173,239,0.18)] [animation-duration:1.8s]" aria-hidden="true" />
              <Icon name={toast.icon || 'Inbox'} size={18} className="relative" />
            </span>
            <span className="min-w-0 flex-1 pr-7">
              <span className="block text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#0284c7]">
                {toast.eyebrow || 'Live update'}
              </span>
              <span className="mt-1 block text-[15px] font-bold leading-snug tracking-tight text-slate-900">
                {toast.title}
              </span>
              {toast.body ? (
                <span className="mt-1 block text-[13px] leading-snug text-slate-600 line-clamp-3">
                  {toast.body}
                </span>
              ) : null}
              <span className="mt-2.5 inline-flex items-center gap-1 text-[12px] font-bold text-[#00adef]">
                {toast.cta || 'Open'}
                <Icon name="ArrowRight" size={13} />
              </span>
            </span>
          </button>

          <button
            type="button"
            onClick={() => onDismiss?.(toast.id)}
            className="absolute right-1.5 top-2.5 inline-flex min-h-[36px] min-w-[36px] items-center justify-center rounded-xl text-slate-300 touch-manipulation hover:bg-slate-100 hover:text-slate-600"
            aria-label="Dismiss"
          >
            <Icon name="X" size={15} />
          </button>
        </div>
      ))}
    </div>
  );
}
